import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getRealEstateReviewQueue,
  resolveRealEstateReviewItem,
} from "../../../services/midpointApi";
import AdminStatePanel from "./layout/AdminStatePanel";

const REVIEW_ACTIONS = [
  { id: "mark_available", label: "Still Available", variant: "primary" },
  { id: "mark_unavailable", label: "Unavailable", variant: "danger" },
  { id: "ignore", label: "Ignore", variant: "default" },
  { id: "archive_source_listing", label: "Archive", variant: "danger" },
  { id: "merge_duplicate", label: "Merge Duplicate", variant: "default", disabled: true },
  { id: "not_duplicate", label: "Not Duplicate", variant: "default" },
  { id: "keep_under_review", label: "Keep Under Review", variant: "default" },
];

const RealEstateReviewQueuePage = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewTypeFilter, setReviewTypeFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notes, setNotes] = useState({});

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getRealEstateReviewQueue({
        status: statusFilter || undefined,
        reviewType: reviewTypeFilter || undefined,
        sourceCode: sourceFilter || undefined,
        limit: 100,
      });
      setItems(data.items || []);
    } catch (loadError) {
      setItems([]);
      setError(loadError.message || "Unable to load review queue.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, reviewTypeFilter, sourceFilter]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleResolve = async (itemId, action) => {
    try {
      setActionError("");
      await resolveRealEstateReviewItem(itemId, { action, notes: notes[itemId] || null });
      setNotes((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      await loadItems();
    } catch (resolveError) {
      setActionError(resolveError.message || "Unable to resolve review item.");
    }
  };

  const kpis = useMemo(() => {
    const total = items.length;
    const high = items.filter((i) => i.priority === "HIGH").length;
    const medium = items.filter((i) => i.priority === "MEDIUM").length;
    const byType = {};
    items.forEach((i) => {
      byType[i.reviewType] = (byType[i.reviewType] || 0) + 1;
    });

    return { total, high, medium, byType };
  }, [items]);

  return (
    <div className="admin-dashboard admin-review-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Availability Review</h1>
          <p className="admin-dashboard__subtitle">
            Listings missing from a broker or stale source rows needing manual verification
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadItems}
            disabled={loading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!loading && !error && (
        <section className="admin-dashboard__kpis">
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Queue Items</span>
            <span className="admin-dash__kpi-value">{kpis.total}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">High Priority</span>
            <span
              className={`admin-dash__kpi-value${kpis.high > 0 ? " admin-dash__kpi-value--red" : ""}`}
            >
              {kpis.high}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Medium Priority</span>
            <span
              className={`admin-dash__kpi-value${kpis.medium > 0 ? " admin-dash__kpi-value--amber" : ""}`}
            >
              {kpis.medium}
            </span>
          </div>
          {Object.entries(kpis.byType).map(([type, count]) => (
            <div className="admin-dash__kpi-card" key={type}>
              <span className="admin-dash__kpi-label">
                {type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
              <span className="admin-dash__kpi-value">{count}</span>
            </div>
          ))}
        </section>
      )}

      {actionError && (
        <div className="admin-re-toast admin-re-toast--error" role="alert">
          {actionError}
        </div>
      )}

      <section className="admin-dash__card admin-dash__card--wide admin-review-dash__queue-card">
        <div className="admin-review-dash__toolbar">
          <div className="admin-review-dash__filters">
            <select
              className="admin-source-controls-dash__select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Open / In progress</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In progress</option>
              <option value="RESOLVED_AVAILABLE">Resolved available</option>
              <option value="RESOLVED_UNAVAILABLE">Resolved unavailable</option>
              <option value="IGNORED">Ignored</option>
            </select>
            <select
              className="admin-source-controls-dash__select"
              value={reviewTypeFilter}
              onChange={(e) => setReviewTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              <option value="CONFLICTING_AVAILABILITY">Conflicting availability</option>
              <option value="STALE_LISTING">Stale listing</option>
              <option value="SOURCE_DISAPPEARED">Source disappeared</option>
            </select>
            <input
              type="text"
              className="admin-source-controls-dash__search"
              placeholder="Source code…"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{ minWidth: "120px" }}
            />
          </div>
          <span className="admin-geocode-dash__item-count">
            {items.length} item{items.length !== 1 ? "s" : ""}
          </span>
        </div>

        {loading ? <AdminStatePanel variant="loading" message="Loading review queue…" /> : null}

        {!loading && error ? (
          <AdminStatePanel variant="error" message={error} onRetry={loadItems} />
        ) : null}

        {!loading && !error && items.length === 0 ? (
          <div className="admin-geocode-dash__empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <p className="muted">No review items match the current filters.</p>
          </div>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div className="admin-review-dash__list">
            {items.map((item) => (
              <article key={item.id} className="admin-review-dash__item">
                <div className="admin-review-dash__item-header">
                  <div className="admin-review-dash__item-title">
                    <span
                      className={`admin-review-dash__priority${
                        item.priority === "HIGH"
                          ? " admin-review-dash__priority--high"
                          : item.priority === "MEDIUM"
                            ? " admin-review-dash__priority--medium"
                            : ""
                      }`}
                    >
                      {item.priority}
                    </span>
                    <h3>{item.canonical?.title || item.sourceListing?.title || "Listing"}</h3>
                  </div>
                  <span className="admin-canonical-dash__status-pill">
                    {item.reviewType?.replace(/_/g, " ")}
                  </span>
                </div>

                <p className="admin-review-dash__reason">{item.reason}</p>
                {item.suggestedAction && (
                  <p className="admin-review-dash__suggestion">{item.suggestedAction}</p>
                )}

                <div className="admin-review-dash__meta-grid">
                  <div>
                    <span className="admin-dash__kpi-label">Source</span>
                    <span>
                      {item.sourceCode}
                      {item.sourceListing?.sourceUrl && (
                        <>
                          {" "}
                          <a href={item.sourceListing.sourceUrl} target="_blank" rel="noreferrer">
                            Open
                          </a>
                        </>
                      )}
                    </span>
                  </div>
                  {item.relatedSourceCode && (
                    <div>
                      <span className="admin-dash__kpi-label">Related Source</span>
                      <span>
                        {item.relatedSourceCode}
                        {item.relatedSourceUrl && (
                          <>
                            {" "}
                            <a href={item.relatedSourceUrl} target="_blank" rel="noreferrer">
                              Open
                            </a>
                          </>
                        )}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="admin-dash__kpi-label">Location</span>
                    <span>
                      {[item.canonical?.neighborhood, item.canonical?.city]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="admin-dash__kpi-label">Not Seen Count</span>
                    <span>{item.sourceListing?.notSeenCount ?? "—"}</span>
                  </div>
                  {item.confidence && (
                    <div>
                      <span className="admin-dash__kpi-label">Confidence</span>
                      <span>{item.confidence}</span>
                    </div>
                  )}
                </div>

                <div className="admin-review-dash__resolve">
                  <textarea
                    className="admin-review-dash__notes"
                    placeholder="Resolution notes (optional)"
                    value={notes[item.id] || ""}
                    onChange={(e) =>
                      setNotes((prev) => ({ ...prev, [item.id]: e.target.value }))
                    }
                    rows={2}
                  />
                  <div className="admin-review-dash__actions">
                    {REVIEW_ACTIONS.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        className={`admin-dashboard__action-btn${
                          action.variant === "primary"
                            ? " admin-dashboard__action-btn--primary"
                            : action.variant === "danger"
                              ? " admin-canonical-dash__action-danger"
                              : ""
                        }`}
                        disabled={action.disabled}
                        title={action.disabled ? "Use Duplicates workflow when enabled" : undefined}
                        onClick={() => handleResolve(item.id, action.id)}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default RealEstateReviewQueuePage;
